import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelLeft24Regular, PanelRight24Regular } from '@fluentui/react-icons';
import { 
  FiPhone, 
  FiCheckCircle, 
  FiXCircle, 
  FiClock, 
  FiBarChart2, 
  FiPieChart, 
  FiTrendingUp, 
  FiActivity,
  FiUsers,
  FiGrid
} from 'react-icons/fi';
import { WIDGET_CATALOG } from './WidgetCatalog';
import './AnalyticsWidgetCatalog.css';

/**
 * AnalyticsWidgetCatalogPanel - Left side panel showing available widgets
 * Similar to CallFlow's NodesPalette but for Analytics widgets
 */
const AnalyticsWidgetCatalogPanel = ({ 
  isOpen = true, 
  onToggle, 
  onWidgetDragStart,
  onWidgetClick,
  availableWidgets = [], // Liste des IDs de widgets disponibles dans le dashboard
  initiallyCollapsed = false // Start collapsed or expanded
}) => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed);

  // Filtrer WIDGET_CATALOG pour ne garder que les widgets du dashboard
  const dashboardWidgets = useMemo(() => {
    if (!availableWidgets || availableWidgets.length === 0) {
      return WIDGET_CATALOG;
    }
    return WIDGET_CATALOG.filter(widget => availableWidgets.includes(widget.id));
  }, [availableWidgets]);

  // Map widget categories to icons
  const categoryIcons = {
    'all': FiGrid,
    'metrics': FiActivity,
    'charts': FiBarChart2,
    'dashboard': FiPhone,
    'analytics': FiTrendingUp,
    'profile': FiUsers,
    'advanced': FiTrendingUp
  };

  // Get categories from widget catalog (filtered)
  const categories = useMemo(() => {
    const cats = new Set(['all']);
    dashboardWidgets.forEach(widget => {
      if (widget.category) {
        cats.add(widget.category);
      }
    });
    return Array.from(cats);
  }, [dashboardWidgets]);

  // Count widgets per category (filtered)
  const categoryCounts = useMemo(() => {
    const counts = { all: dashboardWidgets.length };
    dashboardWidgets.forEach(widget => {
      const cat = widget.category || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [dashboardWidgets]);

  // Filter widgets by active category (from filtered list)
  const filteredWidgets = useMemo(() => {
    if (activeCategory === 'all') {
      return dashboardWidgets;
    }
    return dashboardWidgets.filter(w => w.category === activeCategory);
  }, [activeCategory, dashboardWidgets]);

  // Get category display name
  const getCategoryName = (category) => {
    const names = {
      'all':       t('WIDGET_CAT_ALL'),
      'metrics':   t('WIDGET_CAT_METRICS'),
      'charts':    t('WIDGET_CAT_CHARTS'),
      'dashboard': t('WIDGET_CAT_DASHBOARD'),
      'analytics': t('WIDGET_CAT_ANALYTICS'),
      'profile':   t('WIDGET_CAT_ANALYTICS'),
      'advanced':  t('WIDGET_CAT_ANALYTICS')
    };
    return names[category] || category;
  };

  // Localised name/description with fallback to legacy fields
  const widgetName = (w) => (w.nameKey ? t(w.nameKey) : w.name) || w.name;
  const widgetDesc = (w) => (w.descKey ? t(w.descKey) : w.description) || w.description;

  // Handle drag start
  const handleDragStart = (e, widget) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'widget',
      widgetId: widget.id,
      widget: widget
    }));
    
    if (onWidgetDragStart) {
      onWidgetDragStart(widget);
    }
  };

  // Handle widget click (alternative to drag)
  const handleWidgetClick = (widget) => {
    if (onWidgetClick) {
      onWidgetClick(widget);
    }
  };

  // Toggle collapse
  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    // Ne pas appeler onToggle - cela garderait le panneau ouvert
    // Seul le bouton Edit devrait fermer le catalogue
  };

  if (!isOpen) return null;

  return (
    <div className={`analytics-catalog-wrapper ${isCollapsed ? 'collapsed' : ''}`}>
      <div className={`analytics-catalog-container ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Toggle Button */}
        <div className="analytics-catalog-header-toggle">
          <button
            className="analytics-catalog-toggle-btn"
            onClick={handleToggleCollapse}
            title={isCollapsed ? t('WIDGET_CATALOG_EXPAND') : t('WIDGET_CATALOG_COLLAPSE')}
          >
            {isCollapsed ? <PanelRight24Regular /> : <PanelLeft24Regular />}
          </button>
        </div>
     
        {/* Widgets List */}
        <div className="analytics-widgets-list">
          {filteredWidgets.length === 0 ? (
            <div className="analytics-widgets-empty">
              {t('WIDGET_CATALOG_NO_RESULTS')}
            </div>
          ) : (
            filteredWidgets.map(widget => {
              const Icon = widget.icon || FiGrid;
              const iconColor = widget.color || '#0078D4';
              const name = widgetName(widget);
              const description = widgetDesc(widget);

              return (
                <div
                  key={widget.id}
                  className="analytics-widget-item"
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, widget)}
                  onClick={() => handleWidgetClick(widget)}
                  title={`${name}: ${description}`}
                >
                  <div className="analytics-widget-item-header">
                    <div
                      className="analytics-widget-item-icon"
                      style={{
                        background: `linear-gradient(135deg, ${iconColor}15 0%, ${iconColor}05 100%)`,
                        color: iconColor
                      }}
                    >
                      <Icon />
                    </div>
                    {!isCollapsed && (
                      <div className="analytics-widget-item-title">
                        {name}
                      </div>
                    )}
                  </div>
                  {!isCollapsed && (
                    <div className="analytics-widget-item-description">
                      {description}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsWidgetCatalogPanel;
